import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AiFillMail, AiFillPhone } from "react-icons/ai";
import { BsAwardFill } from "react-icons/bs";
import { MdBuild, MdTimer } from "react-icons/md";
import {
  FaBrain,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTags,
  FaUser,
} from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import { serviceCategories } from "../data/serviceData";
import { mailHref, phoneHref, siteConfig } from "../siteConfig";

const Navbar = () => {
  const isDesktop = useMediaQuery({ query: "(min-width: 768px)" });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isHeaderPinned, setIsHeaderPinned] = useState(false);
  const topBarRef = useRef(null);
  const socialLinks = [
    {
      key: "linkedin",
      label: "LinkedIn",
      href: siteConfig.socialLinks.linkedin,
      icon: <FaLinkedinIn />,
    },
    {
      key: "facebook",
      label: "Facebook",
      href: siteConfig.socialLinks.facebook,
      icon: <FaFacebookF />,
    },
    {
      key: "instagram",
      label: "Instagram",
      href: siteConfig.socialLinks.instagram,
      icon: <FaInstagram />,
    },
  ];

  useEffect(() => {
    if (isDesktop) {
      setIsMenuOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    const handleScroll = () => {
      const topBarHeight = topBarRef.current?.offsetHeight || 0;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || 0;

      setIsHeaderPinned(scrollTop >= topBarHeight);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeNavigation = () => {
    setIsServicesOpen(false);

    if (!isDesktop) {
      setIsMenuOpen(false);
    }
  };

  return (
    <div>
      <div className="bg-gray-100 antialiased">
        <div
          ref={topBarRef}
          className="border-b border-slate-800 bg-[#1A1D2B] text-white"
        >
          <div className="mx-auto flex max-w-screen-xl flex-col gap-3 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between md:px-5 lg:px-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              <div className="flex items-center gap-2 text-blue-200">
                <AiFillPhone className="text-base" />
                <span className="text-xs font-semibold uppercase tracking-[0.18em]">
                  Sales Enquiry
                </span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                <a
                  href={phoneHref(siteConfig.phonePrimary)}
                  className="transition hover:text-blue-200"
                >
                  {siteConfig.phoneDisplay[0]}
                </a>
                <a
                  href={phoneHref(siteConfig.phoneSecondary)}
                  className="transition hover:text-blue-200"
                >
                  {siteConfig.phoneDisplay[1]}
                </a>
                <a
                  href={mailHref(siteConfig.email)}
                  className="flex items-center gap-2 transition hover:text-blue-200"
                >
                  <AiFillMail className="text-base" />
                  <span>{siteConfig.email}</span>
                </a>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 md:justify-end">
              <div className="flex items-center gap-2">
                {socialLinks.map((socialLink) =>
                  socialLink.href ? (
                    <a
                      key={socialLink.key}
                      href={socialLink.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={socialLink.label}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-sm text-white transition hover:bg-white/20 hover:text-blue-200"
                    >
                      {socialLink.icon}
                    </a>
                  ) : (
                    <span
                      key={socialLink.key}
                      aria-label={`${socialLink.label} link coming soon`}
                      title={`${socialLink.label} link coming soon`}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-sm text-white"
                    >
                      {socialLink.icon}
                    </span>
                  )
                )}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white transition hover:bg-blue-700"
              >
                Enquiry Now
              </Link>
            </div>
          </div>
        </div>

        <div className="h-28">
          <div
            className={`w-full bg-white text-gray-700 shadow-sm transition-shadow ${
              isHeaderPinned ? "fixed inset-x-0 top-0 z-[60]" : "relative z-20"
            }`}
          >
            <div className="mx-auto flex max-w-screen-xl flex-col px-4 md:flex-row md:items-center md:justify-between md:px-5 lg:px-6">
            <div className="flex flex-row items-center justify-between p-4">
              <Link
                to="/"
                className="rounded-lg text-lg font-semibold uppercase tracking-widest text-gray-900 focus:outline-none focus:shadow-outline"
                onClick={closeNavigation}
              >
                <img src="/img/logo.png" alt="logo" className="h-20" />
              </Link>
              <button
                className="rounded-lg focus:outline-none focus:shadow-outline md:hidden"
                onClick={() => setIsMenuOpen((current) => !current)}
                aria-label="Toggle navigation menu"
              >
                <svg fill="currentColor" viewBox="0 0 20 20" className="h-6 w-6">
                  {isMenuOpen ? (
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM9 15a1 1 0 011-1h6a1 1 0 110 2h-6a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    ></path>
                  )}
                </svg>
              </button>
            </div>
            <nav
              className={`flex-col flex-grow pb-4 md:flex md:flex-row md:justify-end md:pb-0 ${
                isDesktop || isMenuOpen ? "flex" : "hidden"
              }`}
            >
              <Link
                className="mt-2 bg-transparent px-4 py-2 text-md font-semibold hover:border-b-2 hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:shadow-outline md:mt-0 md:ml-4"
                to="/"
                onClick={closeNavigation}
              >
                Home
              </Link>
              <Link
                className="mt-2 bg-transparent px-4 py-2 text-md font-semibold hover:border-b-2 hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:shadow-outline md:mt-0 md:ml-4"
                to="/about"
                onClick={closeNavigation}
              >
                About US
              </Link>
              <Link
                className="mt-2 border-b-2 border-white bg-transparent px-4 py-2 text-md font-semibold hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:shadow-outline md:mt-0 md:ml-4"
                to="/certificates"
                onClick={closeNavigation}
              >
                Certificates
              </Link>
              <div
                className="relative md:ml-4"
                onMouseEnter={() => isDesktop && setIsServicesOpen(true)}
                onMouseLeave={() => isDesktop && setIsServicesOpen(false)}
              >
                <button
                  type="button"
                  className="mt-2 flex w-full items-center justify-between border-b-2 border-white bg-transparent px-4 py-2 text-left text-md font-semibold hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:shadow-outline md:mt-0 md:w-auto"
                  onClick={() =>
                    setIsServicesOpen((current) =>
                      isDesktop ? true : !current
                    )
                  }
                >
                  <span>Services</span>
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="ml-2 h-4 w-4"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {isServicesOpen ? (
                  <div className="md:absolute md:left-0 md:top-full md:min-w-[270px] md:rounded-md md:border md:border-gray-200 md:bg-white md:py-2 md:shadow-lg">
                    <Link
                      to="/services"
                      onClick={closeNavigation}
                      className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-700"
                    >
                      All Services
                    </Link>
                    {serviceCategories.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        onClick={closeNavigation}
                        className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-700"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
              <Link
                className="mt-2 border-b-2 border-white bg-transparent px-4 py-2 text-md font-semibold hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:shadow-outline md:mt-0 md:ml-4"
                to="/gallery"
                onClick={closeNavigation}
              >
                Gallery
              </Link>
              <Link
                className="mt-2 bg-transparent px-4 py-2 text-md font-semibold hover:border-b-2 hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:shadow-outline md:mt-0 md:ml-4"
                to="/contact"
                onClick={closeNavigation}
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>
        </div>
      </div>
      {FeaturesBar()}
    </div>
  );
};

export default Navbar;

const FeaturesBar = () => {
  const features = [
    {
      icon: <BsAwardFill size={28} />,
      title: "ISO/IEC 17025 : 2017 Certified",
    },
    {
      icon: <MdBuild size={28} />,
      title: "On-Site Calibration",
    },
    {
      icon: <MdTimer size={28} />,
      title: "Rapid Turnaround",
    },
    {
      icon: <FaTags size={28} />,
      title: "All Major Brands",
    },
    {
      icon: <FaUser size={28} />,
      title: "Account Managed",
    },
    {
      icon: <FaBrain size={28} />,
      title: "18 Years Experience",
    },
  ];

  return (
    <div className="hidden justify-center bg-blue-500 py-4 md:flex">
      <div className="flex flex-wrap justify-center gap-8">
        {features.map((feature, index) => (
          <div
            key={index}
            className="flex items-center space-x-1 text-md text-white"
          >
            <div>{feature.icon}</div>
            <span>{feature.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
